import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Divider, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentsIcon from '@mui/icons-material/Payments';
import { NETAPI_URL, PAYMENT_SERVICE_URL, PUBLIC_APP_URL } from '../../config/api';
import { redirectToMokaHost } from '../../utils/mokaPaymentHost';

/**
 * Yenileme/tahsilat ödeme akışının ÇEKİRDEĞİ (chrome'suz içerik).
 *
 * Hem public ödeme sayfası (PublicRenewalPayment — /pay/renewal/:token) hem de panel
 * içi ödeme popup'ı (LicensePaymentDialog — header rozeti) bu bileşeni kullanır.
 *
 * Akış:
 *  1. GET /api/PublicRenewal/by-token/{token} → tahsilat özeti + platformCompanyId.
 *  2. "Güvenli Ödeme Yap" → GTS_Payment_Service /api/payments/initiate
 *     (X-Company-Id = platformCompanyId), referenceType = "license_renewal".
 *     Public sayfada (autoStart) bu adım kayıt yüklenince kendiliğinden çalışır.
 *  3. PayTR iframe gösterilir.
 *  4. /status polling → "success" olunca case yeniden yüklenir; Paid ise teşekkür ekranı
 *     ve onPaid() (çağıran, /api/Company'yi tazeleyip rozeti günceller).
 */

export interface RenewalLine {
  id: number;
  name: string;
  unitPrice: number;
  quantity: number;
  vatRate: number;
  lineTotal: number;
}

export interface PublicRenewalCase {
  token: string;
  status: string;
  companyName?: string | null;
  totalAmount: number;
  currency: string;
  expiryDate?: string | null;
  issuerCompanyTitle?: string | null;
  notifyEmail?: string | null;
  notifyPhone?: string | null;
  platformCompanyId: string;
  /** paytr, moka veya parampos. Boşsa ödeme başlatılamaz. */
  paymentProvider?: string | null;
  lines: RenewalLine[];
}

/** E-posta ödeme sayfasının kullandığı servis. Yerel adres kapalıysa panel buraya düşer. */
const EMAIL_PAYMENT_SERVICE_URL = 'https://gtspayment.aquasoft.com.tr';

const isLocalPaymentUrl = (url: string) => /localhost|127\.0\.0\.1/i.test(url);

const fmtMoney = (v: number, cur: string) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: cur || 'TRY' }).format(v || 0);

const fmtDate = (v?: string | null) => {
  if (!v) return '-';
  try { return new Date(v).toLocaleDateString('tr-TR'); } catch { return '-'; }
};

/**
 * Tahsilat sonrası panele dönüş Aquasoft girişidir.
 * /accounting/dashboard süper admin oturumunu /superadmin paneline savuruyordu.
 * location state taşınmaz; login sonrası yanlış panele düşülmez.
 */
const goToPanel = () => {
  window.location.assign(`${PUBLIC_APP_URL}/#/login`);
};

/** PayTR dönüş adresi sorguyu hash içine veya fragment'ten önceki path'e koyabilir. */
const returnedFromPaytr = () => {
  const hash = window.location.hash || '';
  const qIndex = hash.indexOf('?');
  const hashParams = new URLSearchParams(qIndex >= 0 ? hash.slice(qIndex + 1) : '');
  const topParams = new URLSearchParams(window.location.search);
  const flag = (hashParams.get('payment') || topParams.get('payment') || '').toLowerCase();
  if (flag === 'return' || flag === 'fail') return true;
  return Boolean(hashParams.get('merchantOid') || topParams.get('merchantOid'));
};

/** HashRouter: dönüş adresi # içinde olmalı. Aksi halde path kalır, hash panele kayar. */
const renewalReturnUrl = (payToken: string, payment: 'return' | 'fail', merchantOid?: string) => {
  const base = `${window.location.origin}/#/pay/renewal/${encodeURIComponent(payToken)}?payment=${payment}`;
  return merchantOid ? `${base}&merchantOid=${encodeURIComponent(merchantOid)}` : base;
};

const readReturnParam = (key: string) => {
  const hash = window.location.hash || '';
  const qIndex = hash.indexOf('?');
  const hashParams = new URLSearchParams(qIndex >= 0 ? hash.slice(qIndex + 1) : '');
  const topParams = new URLSearchParams(window.location.search);
  return hashParams.get(key) || topParams.get(key) || '';
};

interface RenewalPaymentFlowProps {
  /** Ödenecek tahsilat kaydının benzersiz token'ı (public /pay/renewal/:token). */
  token: string;
  /** Ödeme onaylanınca (case Paid) bir kez çağrılır — çağıran şirket bilgisini tazeler. */
  onPaid?: () => void;
  /** Paid teşekkür ekranındaki "Kapat" butonu (dialog içinde). Verilmezse gösterilmez. */
  onClose?: () => void;
  /**
   * E-posta linki (/pay/renewal/:token): kayıt yüklenince PayTR iframe'ini
   * ikinci bir "Güvenli Ödeme Yap" tıklaması olmadan açar.
   */
  autoStart?: boolean;
}

const RenewalPaymentFlow: React.FC<RenewalPaymentFlowProps> = ({ token, onPaid, onClose, autoStart = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PublicRenewalCase | null>(null);

  const [initiating, setInitiating] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const paidNotifiedRef = useRef(false);
  const paymentBaseRef = useRef(PAYMENT_SERVICE_URL);

  const loadCase = useCallback(async () => {
    if (!token) return false;
    try {
      const resp = await fetch(`${NETAPI_URL}/api/PublicRenewal/by-token/${encodeURIComponent(token)}`);
      const json = await resp.json().catch(() => null);
      if (!resp.ok || !json?.success) {
        setError(json?.message || 'Ödeme kaydı bulunamadı.');
        setData(null);
      } else {
        const next = json.data as PublicRenewalCase;
        setData(next);
        setError(null);
        return next.status === 'Paid';
      }
    } catch {
      setError('Bağlantı hatası. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
    return false;
  }, [token]);

  useEffect(() => { loadCase(); }, [loadCase]);

  // Ödeme başlatıldıktan sonra durum yoklaması (callback consume'u tetikleyince Paid olur).
  const startPolling = useCallback((oid: string, platformCompanyId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const resp = await fetch(
          `${paymentBaseRef.current}/api/payments/status?merchantOid=${encodeURIComponent(oid)}`,
          { headers: { 'X-Company-Id': platformCompanyId } },
        );
        const json = await resp.json().catch(() => null);
        const status = (json?.status || json?.data?.status || '').toString().toLowerCase();
        if (status === 'success') {
          const paid = await loadCase();
          // Consume callback'ten geç gelebilir. Kayıt Paid olunca yoklamayı kes.
          if (paid && pollRef.current) clearInterval(pollRef.current);
        }
      } catch { /* sessiz - bir sonraki denemede tekrar */ }
    }, 4000);
  }, [loadCase]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const downloadPaperInvoice = useCallback(async () => {
    if (!token) return;
    setDownloadingInvoice(true);
    setInvoiceError(null);
    try {
      const resp = await fetch(
        `${NETAPI_URL}/api/PublicRenewal/by-token/${encodeURIComponent(token)}/paper-invoice`,
      );
      if (!resp.ok) {
        setInvoiceError('Kağıt fatura indirilemedi. Lütfen tekrar deneyin.');
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const header = resp.headers.get('Content-Disposition') || '';
      const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(header);
      a.download = decodeURIComponent(match?.[1] || match?.[2] || 'Fatura.pdf');
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setInvoiceError('Kağıt fatura indirilemedi. Lütfen tekrar deneyin.');
    } finally {
      setDownloadingInvoice(false);
    }
  }, [token]);

  const isPaid = data?.status === 'Paid';

  // Paid'e geçince çağırana bir kez haber ver (şirket bilgisini tazelesin).
  useEffect(() => {
    if (isPaid && !paidNotifiedRef.current) {
      paidNotifiedRef.current = true;
      onPaid?.();
    }
  }, [isPaid, onPaid]);

  const handlePay = useCallback(async () => {
    if (!data || !token) return;
    if (!data.platformCompanyId) {
      setPayError('Ödeme altyapısı yapılandırılmamış. Lütfen bizimle iletişime geçin.');
      return;
    }
    if (!(data.notifyEmail || '').trim() || !(data.notifyPhone || '').trim()) {
      setPayError('Ödeme için e-posta ve telefon gerekli. Tahsilat kaydında bu alanları doldurun.');
      return;
    }
    setInitiating(true);
    setPayError(null);
    try {
      const envelope = {
        schemaVersion: 1,
        referenceType: 'license_renewal',
        payload: { token },
      };
      const provider = (data.paymentProvider || 'paytr').trim().toLowerCase();
      const body = {
        provider,
        referenceType: 'license_renewal',
        amount: data.totalAmount,
        currency: data.currency || 'TRY',
        userEmail: (data.notifyEmail || '').trim(),
        userName: data.companyName || 'Musteri',
        userPhone: (data.notifyPhone || '').trim(),
        basket: data.lines.map((l) => ({
          name: l.name,
          price: l.lineTotal,
          quantity: 1,
        })),
        draftDataJson: JSON.stringify(envelope),
        merchantOkUrl: renewalReturnUrl(token, 'return'),
        merchantFailUrl: renewalReturnUrl(token, 'fail'),
      };
      const postInitiate = (base: string, timeoutMs?: number) => {
        const controller = new AbortController();
        const timer = timeoutMs ? window.setTimeout(() => controller.abort(), timeoutMs) : 0;
        return fetch(`${base}/api/payments/initiate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Company-Id': data.platformCompanyId,
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        }).finally(() => { if (timer) window.clearTimeout(timer); });
      };
      paymentBaseRef.current = PAYMENT_SERVICE_URL;
      let resp: Response;
      try {
        const local = isLocalPaymentUrl(PAYMENT_SERVICE_URL);
        resp = await postInitiate(PAYMENT_SERVICE_URL, local ? 4000 : undefined);
      } catch (err) {
        if (!isLocalPaymentUrl(PAYMENT_SERVICE_URL) || PAYMENT_SERVICE_URL === EMAIL_PAYMENT_SERVICE_URL) throw err;
        paymentBaseRef.current = EMAIL_PAYMENT_SERVICE_URL;
        resp = await postInitiate(EMAIL_PAYMENT_SERVICE_URL);
      }
      const json = await resp.json().catch(() => null);
      if (!resp.ok || !json?.success || (!json.iframeUrl && !json.token)) {
        setPayError(json?.reason || json?.message || 'Ödeme başlatılamadı. Lütfen tekrar deneyin.');
        return;
      }
      const url: string = json.iframeUrl
        || (provider === 'paytr' && json.token ? `https://www.paytr.com/odeme/guvenli/${json.token}` : '');
      if (!url) {
        setPayError('Ödeme sayfası alınamadı. Lütfen tekrar deneyin.');
        return;
      }
      // E-posta sayfası kayıtlı sitedeki köprüye gidebilir. Paneldeki lisans
      // penceresi (onClose var) sayfayı terk etmez; ödeme iframe'i bu dialogda açılır.
      if (!onClose && redirectToMokaHost({
        paymentHostUrl: json.paymentHostUrl,
        iframeUrl: url,
        returnUrl: renewalReturnUrl(token, 'return', json.merchantOid),
      })) {
        return;
      }
      setIframeUrl(url);
      if (json.merchantOid) {
        startPolling(json.merchantOid, data.platformCompanyId);
      }
    } catch {
      setPayError('Ödeme başlatılırken bağlantı hatası oluştu.');
    } finally {
      setInitiating(false);
    }
  }, [data, token, startPolling, onClose]);

  const backFromGateway = returnedFromPaytr();
  const shouldAutoStart = autoStart && !backFromGateway;

  // Bayrak zamanlayıcı içinde set edilir: Strict Mode ilk effect'i iptal edince
  // ödeme iki kez başlatılmaz, ikinci (kalan) çalıştırma iframe'i açar.
  // PayTR dönüşünde yeniden başlatılmaz; callback Paid yazana kadar kayıt yoklanır.
  const autoStartRef = useRef(false);
  useEffect(() => {
    if (!shouldAutoStart || !data || data.status === 'Paid' || iframeUrl || payError) return;
    if (autoStartRef.current) return;
    const timer = window.setTimeout(() => {
      autoStartRef.current = true;
      void handlePay();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [shouldAutoStart, data, iframeUrl, payError, handlePay]);

  const returnPollStarted = useRef(false);
  useEffect(() => {
    if (!backFromGateway || isPaid || !data?.platformCompanyId || returnPollStarted.current) return;
    const oid = readReturnParam('merchantOid');
    if (!oid) return;
    returnPollStarted.current = true;
    startPolling(oid, data.platformCompanyId);
  }, [backFromGateway, isPaid, data, startPolling]);

  useEffect(() => {
    if (!backFromGateway || isPaid) return;
    const timer = window.setInterval(() => { void loadCase(); }, 3000);
    const stop = window.setTimeout(() => window.clearInterval(timer), 30000);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(stop);
    };
  }, [backFromGateway, isPaid, loadCase]);

  return useMemo(() => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (error || !data) {
      return <Alert severity="error" sx={{ mt: 2 }}>{error || 'Kayıt bulunamadı.'}</Alert>;
    }

    if (isPaid) {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CheckCircleIcon sx={{ fontSize: 72, color: 'success.main' }} />
          <Typography variant="h5" sx={{ mt: 2, fontWeight: 700 }}>Tebrikler, ödemeniz alındı</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Teşekkür ederiz. Ürününüz artık lisanslıdır ve ekranınızdaki uyarı kaldırıldı.
          </Typography>
          {invoiceError && <Alert severity="error" sx={{ mt: 2 }}>{invoiceError}</Alert>}
          <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
            {onClose && (
              <Button
                variant="contained"
                disabled={downloadingInvoice}
                onClick={() => { void downloadPaperInvoice(); }}
              >
                {downloadingInvoice ? 'İndiriliyor...' : 'Kağıt faturayı indir'}
              </Button>
            )}
            {onClose ? (
              <Button variant="outlined" onClick={onClose}>
                Kapat
              </Button>
            ) : (
              <Button variant="contained" onClick={goToPanel}>
                Panele dön
              </Button>
            )}
          </Stack>
        </Box>
      );
    }

    if (shouldAutoStart && !payError && !iframeUrl) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
          <CircularProgress />
          <Typography color="text.secondary">Güvenli ödeme ekranı açılıyor...</Typography>
        </Box>
      );
    }

    if (iframeUrl) {
      return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Ödemenizi aşağıdaki güvenli ekrandan tamamlayabilirsiniz. Ödeme onaylandığında bu ekran otomatik güncellenir.
          </Alert>
          <Box
            component="iframe"
            title="Ödeme"
            src={iframeUrl}
            sx={{ width: '100%', minHeight: 600, border: 'none' }}
          />
        </Box>
      );
    }

    return (
      <>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          {data.companyName && <Chip label={data.companyName} color="primary" variant="outlined" />}
          {data.expiryDate && <Chip label={`Bitiş: ${fmtDate(data.expiryDate)}`} variant="outlined" />}
          {data.issuerCompanyTitle && <Chip label={`Fatura: ${data.issuerCompanyTitle}`} variant="outlined" />}
        </Stack>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ürün / Hizmet</TableCell>
                <TableCell align="right">Adet</TableCell>
                <TableCell align="right">Birim</TableCell>
                <TableCell align="right">KDV</TableCell>
                <TableCell align="right">Tutar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.lines.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.name}</TableCell>
                  <TableCell align="right">{l.quantity}</TableCell>
                  <TableCell align="right">{fmtMoney(l.unitPrice, data.currency)}</TableCell>
                  <TableCell align="right">%{l.vatRate}</TableCell>
                  <TableCell align="right">{fmtMoney(l.lineTotal, data.currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Toplam</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {fmtMoney(data.totalAmount, data.currency)}
          </Typography>
        </Box>

        {backFromGateway && (
          <>
            <Alert severity="info" sx={{ mt: 2 }}>
              Ödeme sonucu kontrol ediliyor. Onaylandığında bu ekran kendiliğinden güncellenir.
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              {onClose ? (
                <Button variant="outlined" onClick={onClose}>
                  Kapat
                </Button>
              ) : (
                <Button variant="outlined" onClick={goToPanel}>
                  Panele dön
                </Button>
              )}
            </Box>
          </>
        )}

        {payError && <Alert severity="error" sx={{ mt: 2 }}>{payError}</Alert>}

        <Button
          fullWidth
          size="large"
          variant="contained"
          startIcon={initiating ? <CircularProgress size={20} color="inherit" /> : <PaymentsIcon />}
          disabled={initiating}
          onClick={handlePay}
          sx={{ mt: 3, py: 1.5 }}
        >
          {initiating ? 'Hazırlanıyor...' : 'Güvenli Ödeme Yap'}
        </Button>
      </>
    );
  }, [loading, error, data, isPaid, iframeUrl, payError, initiating, handlePay, onClose, shouldAutoStart, backFromGateway, downloadPaperInvoice, downloadingInvoice, invoiceError]);
};

export default RenewalPaymentFlow;
